import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, finalize, map, Observable, of, shareReplay, startWith, Subscription, switchMap } from 'rxjs';
import { RefreshService } from '../../components/common/refresh-widget/refresh.service';
import { TracesApiService, TraceSearchCriteria, TraceSearchResult } from '../../api/traces-api.service';
import { ChargepointType } from '../../api/chargepoints-api.service';
import { TraceTypeOption, TRACE_TYPE_OPTIONS } from './traces-page.data';

@Component({
  selector: 'app-traces-page',
  templateUrl: './traces-page.component.html',
  styleUrls: ['./traces-page.component.scss'],
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatProgressBarModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatAutocompleteModule,
    MatListModule,
    MatDividerModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatPaginatorModule,
  ]
})
export class TracesPageComponent implements OnDestroy {
  public result$: Observable<TraceSearchResult>;
  public searching = false;
  public page$ = new BehaviorSubject<PageEvent>({ pageIndex: 0, pageSize: 25, length: 0 });
  public filteredTraceTypes$: Observable<TraceTypeOption[]>;

  // Liste des types de traces avec labels user-friendly
  public traceTypeOptions: TraceTypeOption[] = TRACE_TYPE_OPTIONS;

  public searchFormGroup = new FormGroup({
    chargepointId: new FormControl(''),
    type: new FormControl(''),
    chargepointType: new FormControl('all'),
    from: new FormControl<Date | null>(null),
    to: new FormControl<Date | null>(null),
    errorOnly: new FormControl(false),
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private tracesApiService: TracesApiService,
    private router: Router,
    private refreshService: RefreshService,
    private snackBar: MatSnackBar
  ) {
    this.result$ = combineLatest([
      this.refreshService.refreshTrigger,
      this.page$
    ]).pipe(
      switchMap(([_, page]) => this.doSearch(page)),
      startWith({ traces: [], total: 0 }),
      shareReplay(1)
    );

    // Filtrer les options de type de trace selon la saisie
    this.filteredTraceTypes$ = this.searchFormGroup.get('type')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        // Si c'est une valeur technique (déjà sélectionnée), chercher le label correspondant
        const searchValue = typeof value === 'string' ? value : '';
        const filterValue = searchValue.toLowerCase();
        
        // Si la valeur correspond exactement à une valeur technique, retourner toutes les options
        // Sinon, filtrer par label ou value
        const exactMatch = this.traceTypeOptions.find(opt => opt.value === searchValue);
        if (exactMatch) {
          return this.traceTypeOptions;
        }
        
        return this.traceTypeOptions.filter(option =>
          option.label.toLowerCase().includes(filterValue) ||
          option.value.toLowerCase().includes(filterValue)
        );
      })
    );

    this.subscriptions.push(
      this.searchFormGroup.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
      ).subscribe(() => {
        if (this.page$.value.pageIndex !== 0) {
          this.page$.next({ ...this.page$.value, pageIndex: 0 });
        } else {
          this.refreshService.trigger();
        }
      })
    );
  }

  public onPageChange(event: PageEvent) {
    this.page$.next(event);
  }

  private doSearch(page: PageEvent): Observable<TraceSearchResult> {
    const formValue = this.searchFormGroup.value;
    
    // Construire les types à rechercher selon la sélection
    const types: ChargepointType[] = [];
    const chargepointTypeValue = formValue.chargepointType || 'all';
    
    if (chargepointTypeValue === 'all') {
      types.push(ChargepointType.OCPP, ChargepointType.OKECHARGE);
    } else if (chargepointTypeValue === 'OCPP') {
      types.push(ChargepointType.OCPP);
    } else if (chargepointTypeValue === 'OKECHARGE') {
      types.push(ChargepointType.OKECHARGE);
    }

    const searchCriteria: TraceSearchCriteria = {
      chargepointId: formValue.chargepointId && formValue.chargepointId.trim() !== '' 
        ? formValue.chargepointId.trim() 
        : undefined,
      type: formValue.type && formValue.type.trim() !== '' 
        ? formValue.type.trim() 
        : undefined,
      types: types.length > 0 ? types : undefined,
      from: formValue.from || undefined,
      to: formValue.to ? new Date(formValue.to.getTime() + 24 * 60 * 60 * 1000) : undefined,
      errorOnly: formValue.errorOnly || undefined,
      page: page.pageIndex,
      size: page.pageSize,
    };

    // Le backend exige au moins chargepointId ou type
    if (!searchCriteria.chargepointId && !searchCriteria.type && !searchCriteria.from && !searchCriteria.to) {
      // Retourner un observable vide si aucun critère n'est fourni
      this.searching = false;
      return of({ traces: [], total: 0 });
    }

    this.searching = true;
    return this.tracesApiService.searchTraces(searchCriteria).pipe(
      catchError(() => {
        this.snackBar.open('Unable to load traces', 'Close', { duration: 3000 });
        return of({ traces: [], total: 0 });
      }),
      finalize(() => this.searching = false)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public navigateToChargepoint(chargepointId: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/chargepoints', chargepointId])
    );
    window.open(url, '_blank');
  }

  public copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Copied to clipboard', 'Close', {
        duration: 2000,
      });
    });
  }

  public formatPayload(payload: any): string {
    return JSON.stringify(payload, null, 2);
  }

  public displayTraceType(value: string | TraceTypeOption | null): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      // Si c'est une string (valeur technique), chercher l'option correspondante
      const found = this.traceTypeOptions.find(opt => opt.value === value);
      return found ? found.label : value;
    }
    // Si c'est un objet TraceTypeOption
    return value.label;
  }

  public getTraceTypeValue(option: TraceTypeOption | string): string {
    if (typeof option === 'string') {
      return option;
    }
    return option ? option.value : '';
  }

}

